import { GlobalGameStore } from '@game-libs/global-game-store';
import { vec, Vector } from 'excalibur';
import { createNoise2D, NoiseFunction2D } from 'simplex-noise';

export class ShakeObjectComponent {
  private trauma: number = 0;
  private readonly traumaDecay: number;
  private readonly maxOffset: number;
  private readonly maxRotation: number;
  private readonly frequency: number;
  private readonly store = GlobalGameStore();

  private time: number = 0;

  private readonly noise: NoiseFunction2D;
  private readonly seedX: number;
  private readonly seedY: number;
  private readonly seedRot: number;

  constructor(options?: {
    decay?: number;
    maxOffset?: number;
    maxRotation?: number;
    frequency?: number;
  }) {
    this.traumaDecay = options?.decay ?? 1;
    this.maxOffset = options?.maxOffset ?? 20;
    this.maxRotation = options?.maxRotation ?? 0.1;
    this.frequency = options?.frequency ?? 30;

    this.noise = createNoise2D();

    this.seedX = Math.random() * 1000;
    this.seedY = Math.random() * 1000;
    this.seedRot = Math.random() * 1000;
  }

  /**
   * Add trauma (between 0 and 1)
   */
  public addTrauma(amount: number): void {
    this.trauma = Math.min(this.trauma + amount, 1);
  }

  /**
   * Call this every frame with delta time (in seconds)
   */
  public update(delta: number): void {
    this.time += delta;
    this.trauma = Math.max(this.trauma - this.traumaDecay * delta, 0);
  }

  /**
   * Get current shake offset and rotation
   */
  public getShake(): { offset: Vector; rotation: number } {
    const t = this.trauma ** 2;

    const timeX = this.seedX + this.time * this.frequency;
    const timeY = this.seedY + this.time * this.frequency;
    const timeRot = this.seedRot + this.time * this.frequency;

    const shakeIntensityMultiplier =
      this.store.settings.value.shakeIntensity / 100;

    const offset: Vector = vec(
      this.maxOffset * t * this.noise(timeX, 0) * shakeIntensityMultiplier,
      this.maxOffset * t * this.noise(timeY, 0) * shakeIntensityMultiplier
    );

    const rotation =
      this.maxRotation * t * this.noise(timeRot, 0) * shakeIntensityMultiplier;

    return { offset, rotation };
  }
}
